import { useCallback, useMemo, useRef } from 'react';

import styled from 'styled-components';
import { PositionView } from '@paradigm/features/src/positions/entities/positions';
import { usePositionsView } from '@paradigm/features/src/positions/contexts/store';

import LoadingSpinner from '@paradigm/design-system/src/components/LoadingSpinner';
import Table from '@paradigm/design-system/src/components/Table';

import { useDebouncedTrackAction } from '@paradigm/features/src/analytics';
import useColumns from './PositionColumns';
import usePositionTableSettings from './usePositionTableSettings';

export default function PositionTable() {
  const { columns, defaultColumns } = useColumns();
  const { positions, status, error } = usePositionsView();
  const trackAction = useDebouncedTrackAction();
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const { settings } = usePositionTableSettings({ defaultColumns });
  const data = useMemo(() => {
    return positions;
  }, [positions]);

  const trackLoadMore = useCallback(() => {
    trackAction('load more items');
  }, [trackAction]);

  if (status === 'LOADING')
    return (
      <div css="text-align: center; padding: 16px;">
        <LoadingSpinner size={40} alt="loading spinner" />
      </div>
    );
  if (status === 'ERROR')
    return (
      <pre css="color: red; margin-top: 1rem; text-align: center">{error}</pre>
    );
  if (positions.length === 0)
    return (
      <p css="opacity: 0.7; margin-top: 1rem; text-align: center">
        You have no open positions
      </p>
    );

  return (
    <Container aria-label="positions table container">
      <Table<PositionView>
        settings={settings}
        data={data}
        tableContainerRef={tableContainerRef}
        columns={columns}
        getRowTestId={(position) => `table-position-row-${position.id}`}
        fetchMoreOnBottomReached={trackLoadMore}
      />
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  position: relative;
  flex-direction: column;
  height: 100%;
`;
